import { Asset, AssetState } from '@/api/generatedApi';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useCompareCart } from '@/services/compareCart';
import { isComparableAsset, isReadyForCompare, isSameCompareKind, isTextDocument } from '@/utils/models';
import { PATHS } from '@/utils/paths';
import { useState } from 'react';
import { useNavigate, useMatch, useSearchParams } from 'react-router-dom';

const getCompareKindMismatchReason = (selectedAsset: Asset): string => {
  return isTextDocument(selectedAsset)
    ? 'Only text files can be compared with the selected text file'
    : 'Only 3D models can be compared with the selected 3D model';
};

const getDisabledReason = ({ asset, selectedAsset }: { asset?: Asset; selectedAsset?: Asset }): string | undefined => {
  if (!asset) {
    return 'Asset not found';
  } else if (asset.state === AssetState.No3DData) {
    return 'Asset has no 3D data to compare';
  } else if (!isComparableAsset(asset)) {
    return 'Asset must be a 3D model or text file';
  } else if (!isReadyForCompare(asset)) {
    return 'Asset is not finished processing';
  } else if (selectedAsset && !isSameCompareKind(selectedAsset, asset)) {
    return getCompareKindMismatchReason(selectedAsset);
  }
  return undefined;
};

const getTooltip = ({
  isSelected,
  disabledReason,
  isComparePage,
}: {
  isSelected: boolean;
  disabledReason?: string;
  isComparePage: boolean;
}): string => {
  if (isSelected) {
    return isComparePage ? 'Remove from compare' : '';
  } else if (disabledReason) {
    return disabledReason;
  } else {
    return 'Select for compare';
  }
};

export const useCompareButton = ({ asset, quickCompare }: { asset?: Asset; quickCompare?: string }) => {
  const navigate = useNavigate();
  const isComparePage = !!useMatch(PATHS.COMPARE);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { selectedForCompare, setSelectedForCompare, isSelected, getCompareUrl } = useCompareCart();
  const { data, isRemoteSearchTenant } = useGetCurrentUser();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>();
  const { asset: quickCompareAsset } = useGetAsset({ assetId: quickCompare });

  // Compare Viewer is out of scope over the network: it needs the caller's own geometry co-located
  // with the part, so a remote-search tenant has no local asset and the flow fails loud server-side
  // (generate-comparison-viewer-file.ts). Hide the affordance rather than offer a guaranteed error.
  // Per-asset, not per-page — a search can mix local and remote results in one grid.
  const hidden = isRemoteSearchTenant(asset?.tenantId);

  const asset1Id = searchParameters.get('asset1Id');
  const asset2Id = searchParameters.get('asset2Id');
  const isAssetIdInSearchParameters = asset1Id === asset?.id || asset2Id === asset?.id;
  const isSelectedAsset = asset ? isAssetIdInSearchParameters || isSelected(asset.id) : false;
  const disabledReason = getDisabledReason({ asset, selectedAsset: selectedForCompare.asset });
  const disabled = !!disabledReason;
  const tooltip = getTooltip({ isSelected: isSelectedAsset, disabledReason, isComparePage });

  const open = Boolean(anchorElement);
  const hasQuickCompare = quickCompare && quickCompareAsset?.type === 'model';
  const hasSelectedAsset = !!selectedForCompare.asset;

  const shouldShowPopover = (hasQuickCompare || hasSelectedAsset) && !isSelectedAsset && !isComparePage;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!asset) {
      return;
    }

    if (isComparePage) {
      toggleSelection();
    } else if (isSelectedAsset) {
      setSelectedForCompare({});
    } else if (shouldShowPopover) {
      setAnchorElement(event.currentTarget);
    } else {
      handleSelectForCompare();
    }
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleSelectForCompare = () => {
    if (!asset) {
      return;
    }
    setSelectedForCompare({ asset });
    handleClose();
  };

  const handleQuickCompare = () => {
    if (!asset || !quickCompareAsset) {
      return;
    }

    const compareUrl = getCompareUrl(quickCompareAsset, asset, data?.user?.settings);
    navigate(compareUrl);
    handleClose();
  };

  const handleCompareToSelected = () => {
    if (!asset || !selectedForCompare.asset) {
      return;
    }

    const compareUrl = getCompareUrl(selectedForCompare.asset, asset, data?.user?.settings);
    navigate(compareUrl);
    handleClose();
  };

  const toggleSelection = () => {
    if (!asset) {
      return;
    }

    if (asset1Id !== asset.id && asset2Id !== asset.id) {
      if (selectedForCompare.asset) {
        handleCompareToSelected();
      } else {
        handleSelectForCompare();
        const newParameters = new URLSearchParams(searchParameters);
        newParameters.set('asset1Id', asset.id);
        newParameters.set('tenant1Id', asset.tenantId);
        setSearchParameters(newParameters, { replace: true });
      }
      handleCompareToSelected();
      return;
    }

    if (asset1Id === asset.id) {
      const newParameters = new URLSearchParams(searchParameters);
      newParameters.delete('asset1Id');
      const tenant1Id = searchParameters.get('tenant1Id');
      if (tenant1Id) {
        newParameters.delete('tenant1Id');
      }
      setSearchParameters(newParameters, { replace: true });
      setSelectedForCompare({});
    }

    if (asset2Id === asset.id) {
      const newParameters = new URLSearchParams(searchParameters);
      newParameters.delete('asset2Id');
      const tenant2Id = searchParameters.get('tenant2Id');
      if (tenant2Id) {
        newParameters.delete('tenant2Id');
      }
      setSearchParameters(newParameters, { replace: true });
    }
  };

  return {
    anchorElement,
    disabled,
    handleClick,
    handleClose,
    handleQuickCompare,
    handleCompareToSelected,
    handleSelectForCompare,
    hasQuickCompare,
    hasSelectedAsset,
    hidden,
    isSelected: isSelectedAsset,
    open,
    quickCompareAsset,
    selectedAsset: selectedForCompare.asset,
    shouldShowPopover,
    tooltip,
  };
};
